import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, Ticket, DollarSign, CheckCircle, Users, LogOut, Plus, Edit, Archive, Trash2, ScanLine } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/common";
import { EventFormModal } from "@/components/admin/EventFormModal";
import { BookingsView } from "@/components/admin/BookingsView";
import { ArtistManager } from "@/components/admin/ArtistManager";
import { GalleryManager } from "@/components/admin/GalleryManager";
import { QRScanner } from "@/components/admin/QRScanner";
import { SettingsManager } from "@/components/admin/SettingsManager";
import { StaffManager } from "@/components/admin/StaffManager";
import { FoundersManager } from "@/components/admin/FoundersManager";

const ADMIN_TABS = [
  { id: "overview", label: "Overview" },
  { id: "events", label: "Events" },
  { id: "bookings", label: "Bookings" },
  { id: "scanner", label: "QR Scanner" },
  { id: "artists", label: "Artists" },
  { id: "gallery", label: "Gallery" },
  { id: "founders", label: "Founders" },
  { id: "settings", label: "Settings" },
  { id: "staff", label: "Staff" },
];
const STAFF_TABS = [{ id: "scanner", label: "QR Scanner" }];

export default function AdminDashboard() {
  const { admin, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const isStaff = admin?.role === "staff";
  const tabs = isStaff ? STAFF_TABS : ADMIN_TABS;
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [artists, setArtists] = useState([]);
  const [modalEvent, setModalEvent] = useState(undefined); // undefined=closed, null=new, obj=edit
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!authLoading && !admin) navigate("/admin/login");
  }, [authLoading, admin, navigate]);

  useEffect(() => { if (isStaff) setTab("scanner"); }, [isStaff]);

  const loadAll = async () => {
    const [s, e, a] = await Promise.all([
      api.get("/admin/stats"), api.get("/admin/events"), api.get("/artists"),
    ]);
    setStats(s.data); setEvents(e.data); setArtists(a.data);
    setReady(true);
  };

  useEffect(() => {
    if (!admin) return;
    if (isStaff) { setReady(true); return; }
    loadAll().catch(() => toast.error("Failed to load data"));
    /* eslint-disable-next-line */
  }, [admin]);

  const archive = async (id) => {
    try { await api.post(`/admin/events/${id}/archive`); toast.success("Archived to Past Events"); loadAll(); }
    catch { toast.error("Archive failed"); }
  };
  const delEvent = async (id) => {
    if (!window.confirm("Delete this event entirely (including photos and bookings)?")) return;
    try { await api.delete(`/admin/events/${id}`); toast.success("Event deleted"); loadAll(); }
    catch { toast.error("Delete failed"); }
  };

  if (authLoading || !admin || !ready) return <div className="min-h-screen bg-[#0A0A0C]"><Spinner /></div>;

  return (
    <div className="min-h-screen bg-[#0A0A0C]" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-[#D4AF37]/20 bg-[#0A0A0C]/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-8">
          <div>
            <h1 className="font-serif text-xl font-bold gold-text">SIRAKH {isStaff ? "STAFF" : "ADMIN"}</h1>
            <p className="text-xs text-zinc-500">{admin.email}{isStaff ? " · Scanner access" : ""}</p>
          </div>
          <button onClick={() => { logout(); navigate("/admin/login"); }} className="btn-outline !py-2.5 text-sm" data-testid="admin-logout-button"><LogOut size={15} /> Logout</button>
        </div>
        <div className="mx-auto max-w-7xl overflow-x-auto px-4 sm:px-8">
          <div className="flex gap-1 pb-2">
            {tabs.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition-colors ${tab === t.id ? "bg-[#D4AF37]/15 text-[#E5C158]" : "text-zinc-400 hover:text-white"}`}
                data-testid={`admin-tab-${t.id}`}>{t.label}</button>
            ))}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-8">
        {tab === "overview" && (
          <div data-testid="admin-overview">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
              <Stat icon={Calendar} label="Events" value={stats.events} />
              <Stat icon={Users} label="Bookings" value={stats.bookings} />
              <Stat icon={Ticket} label="Tickets Sold" value={stats.tickets_sold} />
              <Stat icon={DollarSign} label="Revenue" value={`₹${stats.revenue}`} />
              <Stat icon={CheckCircle} label="Used Tickets" value={stats.used_tickets} />
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={() => setModalEvent(null)} className="btn-gold" data-testid="admin-create-event-button"><Plus size={16} /> Create Event</button>
              <button onClick={() => setTab("scanner")} className="btn-outline"><ScanLine size={16} /> QR Scanner</button>
            </div>
          </div>
        )}

        {tab === "events" && (
          <div data-testid="admin-events">
            <div className="mb-5 flex justify-end">
              <button onClick={() => setModalEvent(null)} className="btn-gold !py-2.5 text-sm" data-testid="events-create-button"><Plus size={15} /> Create Event</button>
            </div>
            <div className="space-y-3">
              {events.map((e) => (
                <div key={e.id} className="card-dark flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`admin-event-${e.id}`}>
                  <div>
                    <p className="font-serif text-lg font-semibold">{e.name} {e.featured && <span className="ml-2 rounded-full bg-[#D4AF37]/20 px-2 py-0.5 text-[10px] uppercase text-[#E5C158]">Featured</span>}</p>
                    <p className="text-xs text-zinc-500">{e.status} • {e.date || "no date"} • {(e.ticket_categories || []).length} categories</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setModalEvent(e)} className="btn-outline !px-3 !py-2 text-xs" data-testid={`edit-event-${e.id}`}><Edit size={14} /> Edit</button>
                    {e.status !== "past" && <button onClick={() => archive(e.id)} className="btn-outline !px-3 !py-2 text-xs" data-testid={`archive-event-${e.id}`}><Archive size={14} /> Archive</button>}
                    <button onClick={() => delEvent(e.id)} className="btn-outline !border-red-500/40 !px-3 !py-2 text-xs !text-red-400" data-testid={`delete-event-${e.id}`}><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
              {!events.length && <p className="text-sm text-zinc-500">No events yet. Create your first event.</p>}
            </div>
          </div>
        )}

        {tab === "bookings" && <BookingsView events={events} />}
        {tab === "scanner" && <QRScanner />}
        {tab === "artists" && <ArtistManager artists={artists} reload={loadAll} />}
        {tab === "gallery" && <GalleryManager events={events} reload={loadAll} />}
        {tab === "founders" && <FoundersManager />}
        {tab === "settings" && <SettingsManager events={events} />}
        {tab === "staff" && <StaffManager />}
      </main>

      {modalEvent !== undefined && (
        <EventFormModal event={modalEvent} artists={artists}
          onClose={() => setModalEvent(undefined)}
          onSaved={() => { setModalEvent(undefined); loadAll(); }} />
      )}
    </div>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div className="card-dark p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <Icon className="text-[#D4AF37]" size={20} />
    <p className="mt-3 font-serif text-3xl font-bold text-white">{value}</p>
    <p className="text-xs uppercase tracking-wider text-zinc-500">{label}</p>
  </div>
);
